/**
 * Hauptphasen-Helpers für den Akten-Workflow — Issue #415
 *
 * Phasen 1–6: Auftrag · Besichtigung · Gutachten · Versand · Rechnung · Bezahlt
 *
 * Sub-Status-Badges (nur anzeigen wenn Wert ≠ Default):
 *   - Zahlungsstatus (default: offen)
 *   - Klage-Status   (default: keine)
 *   - Warten-auf     (default: null)
 *   - Stellungnahme-Status existiert in akten-stellungnahme (aus #246)
 */

export const HAUPTPHASEN_LABELS: ReadonlyArray<string> = [
  'Auftrag',
  'Besichtigung',
  'Gutachten',
  'Versand',
  'Rechnung',
  'Bezahlt',
] as const;

export type Zahlungsstatus =
  | 'offen'
  | 'teilweise_bezahlt'
  | 'bezahlt'
  | 'gekuerzt'
  | 'ueberzahlt';

export type KlageStatus =
  | 'keine'
  | 'mahnbescheid_gestellt'
  | 'klage_erhoben'
  | 'beweisaufnahme'
  | 'urteil'
  | 'berufung'
  | 'rechtskraeftig';

export type WartenAuf =
  | 'warten_auf_kunde'
  | 'warten_auf_werkstatt'
  | 'warten_auf_anwalt'
  | 'warten_auf_versicherung';

export function zahlungsstatusLabel(status: Zahlungsstatus | string | null | undefined): string | null {
  switch (status) {
    case 'teilweise_bezahlt': return 'Teilweise bezahlt';
    case 'bezahlt':           return 'Bezahlt';
    case 'gekuerzt':          return 'Gekürzt';
    case 'ueberzahlt':        return 'Überzahlt';
    case 'offen':
    default:
      return null;
  }
}

export function zahlungsstatusBadgeClasses(status: Zahlungsstatus | string | null | undefined): string | null {
  switch (status) {
    case 'teilweise_bezahlt': return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'bezahlt':           return 'bg-green-50 text-green-700 border border-green-200';
    case 'gekuerzt':          return 'bg-red-50 text-red-700 border border-red-200';
    case 'ueberzahlt':        return 'bg-purple-50 text-purple-700 border border-purple-200';
    default:
      return null;
  }
}

export function klageStatusLabel(status: KlageStatus | string | null | undefined): string | null {
  switch (status) {
    case 'mahnbescheid_gestellt': return 'Mahnbescheid';
    case 'klage_erhoben':         return 'Klage erhoben';
    case 'beweisaufnahme':        return 'Beweisaufnahme';
    case 'urteil':                return 'Urteil';
    case 'berufung':              return 'Berufung';
    case 'rechtskraeftig':        return 'Rechtskräftig';
    case 'keine':
    default:
      return null;
  }
}

export function klageStatusBadgeClasses(status: KlageStatus | string | null | undefined): string | null {
  switch (status) {
    case 'mahnbescheid_gestellt': return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'klage_erhoben':         return 'bg-red-50 text-red-700 border border-red-200';
    case 'beweisaufnahme':        return 'bg-red-50 text-red-700 border border-red-200';
    case 'urteil':                return 'bg-red-50 text-red-700 border border-red-200';
    case 'berufung':              return 'bg-orange-50 text-orange-700 border border-orange-200';
    case 'rechtskraeftig':        return 'bg-green-50 text-green-700 border border-green-200';
    default:
      return null;
  }
}

export function wartenAufLabel(status: WartenAuf | string | null | undefined): string | null {
  switch (status) {
    case 'warten_auf_kunde':       return 'Warten auf Kunde';
    case 'warten_auf_werkstatt':   return 'Warten auf Werkstatt';
    case 'warten_auf_anwalt':      return 'Warten auf Anwalt';
    case 'warten_auf_versicherung':return 'Warten auf Versicherung';
    default:
      return null;
  }
}

export function formatHauptphaseDatum(datum: string | null | undefined): string | null {
  if (!datum) return null;
  const d = new Date(datum);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
